/**
 * メモに埋め込んだ Todo の取得キャッシュ。
 *
 * - 同じ tick に要求された id はまとめて GET /todos?ids= で取得する
 * - 表示は useTodoStore と比べて updated_at が新しい方を使う (Todo ページ側の更新も拾う)
 * - SSE の todo.* イベントでキャッシュ中の id を取り直す
 */

import { useEffect } from 'react'
import { create } from 'zustand'

import { listTodos } from '@/api/services/todo'
import { registerSyncListener } from '@/api/sync-events'
import type { TodoResponse, TodoStatus } from '@/api/types/todo'
import { parseUtcDate } from '@/lib/date-utils'
import { useTodoStore } from '@/store/useTodoStore'

export type LinkedTodoEntry = TodoResponse | 'missing' | 'error'

type CacheState = {
  entries: Map<number, LinkedTodoEntry>
}

const useLinkedTodoCache = create<CacheState>(() => ({ entries: new Map() }))

const setEntries = (pairs: [number, LinkedTodoEntry][]) => {
  useLinkedTodoCache.setState((state) => {
    const entries = new Map(state.entries)
    for (const [id, entry] of pairs) entries.set(id, entry)
    return { entries }
  })
}

const FETCH_CHUNK_SIZE = 200
const queued = new Set<number>()
const inFlight = new Set<number>()
let flushTimer: ReturnType<typeof setTimeout> | null = null

const fetchChunk = async (ids: number[]) => {
  for (const id of ids) inFlight.add(id)
  try {
    const list = await listTodos({ ids: ids.join(','), limit: ids.length })
    const found = new Map(list.map((t) => [t.id, t]))
    setEntries(ids.map((id) => [id, found.get(id) ?? 'missing']))
  } catch (err) {
    console.warn('[todo-link] failed to fetch todos', err)
    setEntries(ids.map((id) => [id, 'error']))
  } finally {
    for (const id of ids) inFlight.delete(id)
  }
}

const flush = () => {
  flushTimer = null
  const ids = [...queued]
  queued.clear()
  for (let i = 0; i < ids.length; i += FETCH_CHUNK_SIZE) {
    void fetchChunk(ids.slice(i, i + FETCH_CHUNK_SIZE))
  }
}

export const requestLinkedTodo = (id: number) => {
  if (queued.has(id) || inFlight.has(id)) return
  queued.add(id)
  if (flushTimer === null) flushTimer = setTimeout(flush, 0)
}

export const putLinkedTodo = (todo: TodoResponse) => setEntries([[todo.id, todo]])

const isNewer = (a: TodoResponse, b: TodoResponse) =>
  parseUtcDate(a.updated_at).getTime() > parseUtcDate(b.updated_at).getTime()

/** todoId の Todo を返す。未取得なら undefined */
export const useLinkedTodo = (todoId: number | null): LinkedTodoEntry | undefined => {
  const cached = useLinkedTodoCache((s) => (todoId === null ? undefined : s.entries.get(todoId)))
  const fromStore = useTodoStore((s) => (todoId === null ? undefined : s.todos.find((t) => t.id === todoId)))

  useEffect(() => {
    if (todoId !== null) requestLinkedTodo(todoId)
  }, [todoId])

  if (cached === 'missing') return cached
  if (cached === undefined || cached === 'error') return fromStore ?? cached
  if (fromStore && isNewer(fromStore, cached)) return fromStore
  return cached
}

/** 完了 ↔ 未完了を切り替える。Todo ページと同じく未完了に戻すときは pending にする */
export const toggleLinkedTodo = async (todo: TodoResponse): Promise<TodoResponse> => {
  const status: TodoStatus = todo.status === 'done' ? 'pending' : 'done'
  const updated = await useTodoStore.getState().patchTodo(todo.id, { status })
  putLinkedTodo(updated)
  return updated
}

export const createLinkedTodo = async (title: string, project: string | null): Promise<TodoResponse> => {
  const created = await useTodoStore.getState().addTodo({ title, project, source: 'manual' })
  putLinkedTodo(created)
  return created
}

registerSyncListener((event) => {
  if (event.type !== 'todo.created' && event.type !== 'todo.updated' && event.type !== 'todo.deleted') return
  if (!useLinkedTodoCache.getState().entries.has(event.id)) return
  if (event.type === 'todo.deleted') {
    setEntries([[event.id, 'missing']])
    return
  }
  requestLinkedTodo(event.id)
})
